import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import { resetPassword, AuthError } from '../lib/auth';
import { Button, Card, CardBody } from '../components/ui';
import { PasswordInput } from '../components/forms/PasswordInput';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  const navigate = useNavigate();
  const { error: showError, success: showSuccess } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (!token) {
      setTokenError(true);
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword) {
      showError(t('auth.newPassword') + ' ' + t('common.required').toLowerCase());
      return;
    }

    if (newPassword !== confirmPassword) {
      showError(t('auth.passwordsDoNotMatch'));
      return;
    }

    // Basic password validation
    if (newPassword.length < 8) {
      showError(t('auth.passwordRequirements'));
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(token!, newPassword);
      setIsSuccess(true);
      showSuccess(t('auth.passwordChanged'));
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.code === 'RESET_FAILED' || err.message.includes('expired')) {
          setTokenError(true);
        }
        showError(err.message);
      } else {
        showError(t('errors.serverError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900">Leal Chapa</h1>
          <p className="mt-2 text-gray-600">Sistema de Gestion</p>
        </div>

        <Card>
          <CardBody>
            {tokenError ? (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t('auth.tokenExpired')}
                  </h2>
                </div>

                <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
                  <p>El enlace ha expirado o no es valido. Solicite uno nuevo.</p>
                </div>

                <div className="text-center">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Solicitar nuevo enlace
                  </Link>
                </div>
              </div>
            ) : isSuccess ? (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t('auth.passwordChanged')}
                  </h2>
                </div>

                <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700">
                  <p>Su contrasena ha sido restablecida. Redirigiendo al login...</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t('auth.resetPassword')}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Ingrese su nueva contrasena.
                  </p>
                </div>

                <PasswordInput
                  label={t('auth.newPassword')}
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="********"
                  autoComplete="new-password"
                  showRequirements
                  required
                />

                <PasswordInput
                  label={t('auth.confirmPassword')}
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="********"
                  autoComplete="new-password"
                  required
                />

                <Button
                  type="submit"
                  className="w-full"
                  loading={isLoading}
                >
                  {isLoading ? 'Guardando...' : t('auth.resetPassword')}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
                  >
                    {t('common.back')} {t('auth.login').toLowerCase()}
                  </Link>
                </div>
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
